import { NavigateOptions, useLocation, useNavigate } from "react-router";

const SAFE_CODES: Record<string, string | undefined> = {
  "%3A": ":",
  "%3B": ";",
  "%2C": ",",
};
function cleanUrl(url: string): string {
  return url.replace(/%[0-9A-Z]{2,2}/g, (x) => SAFE_CODES[x] ?? x);
}

export type UrlQuery = Record<string, string | undefined>;

export function urlQueryFromSearchParams(usp: URLSearchParams): UrlQuery {
  return Object.fromEntries(usp.entries());
}

export function urlQueryToSearchParams(params: UrlQuery): URLSearchParams {
  return new URLSearchParams(
    Array.from(Object.entries(params))
      .filter(([_k, v]) => v != null)
      .map(([k, v]) => [k, v ?? ""]),
  );
}

export function useUrlQuery(): UrlQuery {
  const loc = useLocation();
  const usp = new URLSearchParams(loc.search);
  const obj = urlQueryFromSearchParams(usp);
  return obj;
}

export function useWithUrlQuery() {
  const loc = useLocation();

  return (params: Record<string, string | undefined>) => {
    const usp = urlQueryToSearchParams(params);
    const search = cleanUrl(usp.toString());
    return loc.pathname + (search ? "?" + search : "");
  };
}

export function useSetUrlQuery() {
  const nav = useNavigate();
  const withUrlQuery = useWithUrlQuery();

  return (
    params: Record<string, string | undefined>,
    options?: NavigateOptions,
  ) => {
    nav(withUrlQuery(params), options);
  };
}

export function makeUrl(opts: { path: string; search?: UrlQuery }) {
  let url = opts.path;
  if (opts.search) {
    url += "?";
    url += urlQueryToSearchParams(opts.search).toString();
  }
  return url;
}
